import React, {PureComponent} from 'react';
import {Switch, Route} from 'react-router-dom';
import MediaQuery from 'react-responsive';
import styled from 'styled-components';
import FileSaver from 'filesaver.js';
import {NotificationManager} from 'react-notifications';

import * as api from '../../lib/api';
import {withUser} from '../../lib/user';
import * as flags from '../../lib/flags';
import Screen from '../../components/Screen';
import Loading from '../../components/Loading';
import Error from '../../components/Error';
import Notification from '../../components/Notification';
import Content from '../../components/Content';
import Tabs from '../../components/Tabs';
import Button from '../../components/Button';
import Menu from '../../components/Menu';
import Icon from '../../components/Icon';
import ExportFormDataModal from '../../components/ExportFormDataModal';
import BulkPrintLabelsModal from '../../components/BulkPrintLabelsModal';
import { T } from '../../components/I18n';
import CanReviewTemplateData from '../../security/rules/CanReviewTemplateData';
import CanSecondLevelReviewTemplateData from '../../security/rules/CanSecondLevelReviewTemplateData';

import {MAX_ROWS, MAX_SHEETS, DEFAULT_SHEETS} from './shared';
import SheetSelector from './SheetSelector';
import SheetRowFilter from './SheetRowFilter';
import SheetDateRange from './SheetDateRange';
import SheetDashboardTab from './SheetDashboardTab';
import ViewDataTab from './ViewDataTab';
import BulkReviewModal from './BulkReviewModal';
import { CancelController, CancelledException } from '../../lib/cancel';
import FilterInfoMessage from './FilterInfoMessage';

// How often a dashboard showing all sheets looks for sheets created since it loaded. Matches how often the Recent
// Submissions widget sweeps every sheet, so a new sheet is picked up about as quickly as a quiet one waking up.
const NEW_SHEETS_INTERVAL = 30000;
// How many of the newest sheets each check looks at. More than this being created between two checks is unlikely.
const NEW_SHEETS_PAGE_SIZE = 10;

class SheetScreen extends PureComponent {
  state = {
    template: null,
    loading: true,
    failed: false,
    tooManyRows: false,
    rowsBySheetId: {},
    filters: null,
    actionsOpen: false,
    manageSheets: false,
    filterRows: false,
    exportForm: false,
    exportSheets: false,
    pinnedWidgets: [],
    numberOfSheetsLoaded: 0,
    showDeleted: false,
    exportDeleted: false,
    bulkPrintRowIds: null, // Ordered row ids for the bulk label print modal while it is open.
    bulkReviewState: null,
    bulkDeleting: false, // View Data is deleting selected rows, so the options that reload the screen are disabled.
  };

  cancelController = new CancelController();

  componentDidMount() {
    this.load();
  }

  componentWillUnmount() {
    // Cancel any in-progress loading.
    this.cancelController.cancel();
    clearTimeout(this.newSheetsHandle);
  }

  componentDidUpdate(prevProps, prevState) {
    const {match: {params: {sheetIds}}} = this.props;
    const {match: {params: {sheetIds: prevSheetIds}}} = prevProps;
    const {filters} = this.state;
    const {filters: prevFilters} = prevState;

    if (sheetIds !== prevSheetIds || filters !== prevFilters) {
      // Picking other sheets is a navigation, which View Data's prompt has already asked to stop a bulk delete for.
      if (sheetIds !== prevSheetIds) {
        this.bulkDeleting = false;
      }
      this.load();
    }
  }

  // A reload unmounts View Data, which stops a bulk delete part way, so it asks first the way leaving the page does.
  // The options that reload are disabled during a delete, so this only catches a path that missed that.
  confirmStopBulkDelete() {
    // eslint-disable-next-line
    if (this.bulkDeleting && !confirm('Rows are still being deleted. Reload the data and stop deleting?')) {
      return false;
    }

    this.bulkDeleting = false;
    return true;
  }

  handleBulkDeleteChange = deleting => {
    // Kept on the instance too, since a reload has to see a delete that started in this same tick.
    this.bulkDeleting = deleting;
    this.setState({bulkDeleting: deleting});
  }

  async load() {
    if (!this.confirmStopBulkDelete()) {
      return;
    }

    this.cancelController.cancel(); // Cancel any previous load invocations.
    this.cancelController = new CancelController();
    clearTimeout(this.newSheetsHandle);
    const cancellable = promise => this.cancelController.cancellable(promise);
    // A bulk delete stopped by this reload still finishes the batch it had sent, and the rows fetched here can be read
    // before that batch lands, so rows reported deleted while loading are left out of what was fetched.
    const deletedRowIds = this.rowsDeletedDuringLoad = new Set();

    this.setState({loading: true, tooManyRows: false, numberOfSheetsLoaded: 0});

    const {credentials, match: {params: {templateId, sheetIds: sheetIdsAsString}}} = this.props;
    let {filters} = this.state;

    if(filters === null && this.props.location.state && this.props.location.state.filter) {
      filters = JSON.parse(this.props.location.state.filter);

      if(filters != null) {
        if(filters.maxDate) {
          filters.maxDate = new Date(filters.maxDate);
        }
        if(filters.minDate) {
          filters.minDate = new Date(filters.minDate);
        }
      }

      this.setState({filters});
    }

    const sheetIds = sheetIdsAsString.split(',');

    try {
      const template = await cancellable(api.getTemplate(templateId, credentials));
      const catalog = await cancellable(api.getCatalog(credentials));
      const pinnedWidgets = await cancellable(api.getPinnedWidgets(credentials));

      this.setState({
        pinnedWidgets: pinnedWidgets,
        catalog,
        template: {
          ...template,
          contextQuestions: [...template.contextQuestions].sort((a, b) => a.order - b.order),
          questions: [...template.questions].sort((a, b) => a.order - b.order)
        }
      });

      // Load the sheets.
      let unfilteredSheets = [];

      if (sheetIdsAsString === 'all') {
        // Get the last DEFAULT_SHEETS sheets.
        unfilteredSheets = (await cancellable(api.getSheets(templateId, 0, DEFAULT_SHEETS, "dates.lastModifiedDate,desc", credentials))).content;
      }
      else {
        if (sheetIds.length > MAX_SHEETS) {
          this.setState({tooManyRows: true, template, sheets: [], loading: false});
          return;
        }

        for (let sheetId of sheetIds) {
          unfilteredSheets.push(await cancellable(api.getSheet(sheetId, credentials)));
        }
      }

      const sheets = unfilteredSheets
        .filter(sheet => sheet.templateId === templateId)
        .sort((a, b) => b.dates.lastModified - a.dates.lastModified);
      
      this.setState({sheets});

      // Now for each sheet, load all of it's rows. We will do this serially to prevent excessive requests on the API.
      let rowsBySheetId = {};
      let rowCount = 0;

      for (let sheet of sheets) {
        const rows = await cancellable(api.getAllSheetRows(sheet, filters, credentials, this.cancelController));
        rowsBySheetId[sheet.id] = rows;
        rowCount += rows.length;
        sheet.rowCount = rows.length;

        if (rowCount > MAX_ROWS) {
          this.setState({tooManyRows: true, template, sheets, loading: false});
          return;
        }

        this.setState(prevState => ({numberOfSheetsLoaded: prevState.numberOfSheetsLoaded + 1}));
      }

      if (deletedRowIds.size > 0) {
        for (let sheet of sheets) {
          rowsBySheetId[sheet.id] = rowsBySheetId[sheet.id].filter(row => !deletedRowIds.has(row.id));
          sheet.rowCount = rowsBySheetId[sheet.id].length;
        }
      }

      this.rowsDeletedDuringLoad = null;
      this.setState({rowsBySheetId, loading: false});

      // A dashboard picked from a list of sheets shows those sheets, but one showing all of them should take in
      // sheets created while it is open, or their submissions never appear until the page is reloaded.
      if (sheetIdsAsString === 'all') {
        this.scheduleNewSheetsCheck();
      }
    }
    catch(e) {
      if (!(e instanceof CancelledException)) {
        console.error(e);
        this.setState({failed: true, loading: false});
      }
    }
  }

  scheduleNewSheetsCheck() {
    clearTimeout(this.newSheetsHandle);
    this.newSheetsHandle = setTimeout(this.checkForNewSheets, NEW_SHEETS_INTERVAL);
  }

  checkForNewSheets = async () => {
    // Held for this check, so a reload starting part way through cancels it rather than having it add sheets to the
    // dashboard the reload is building.
    const cancelController = this.cancelController;
    const cancellable = promise => cancelController.cancellable(promise);
    const {credentials, match: {params: {templateId}}} = this.props;

    try {
      // Nobody is looking, so there is nothing to keep up to date until the page is shown again.
      if (!document.hidden) {
        // Newest created rather than newest modified: a sheet's modified date comes from the rows put on it, which a
        // device can date in the past, but its created date is set by the API when the sheet is made.
        const {sheets} = this.state;
        const knownSheetIds = new Set(sheets.map(sheet => sheet.id));
        const newest = (await cancellable(
          api.getSheets(templateId, 0, NEW_SHEETS_PAGE_SIZE, 'dates.createdDate,desc', credentials))).content;
        const newSheets = newest.filter(sheet => sheet.templateId === templateId && !knownSheetIds.has(sheet.id));

        if (newSheets.length > 0) {
          const {filters} = this.state;
          let rowCount = Object.keys(this.state.rowsBySheetId)
            .reduce((count, sheetId) => count + this.state.rowsBySheetId[sheetId].length, 0);
          const added = [];
          const addedRowsBySheetId = {};

          for (let sheet of newSheets) {
            const rows = await cancellable(api.getAllSheetRows(sheet, filters, credentials, cancelController));

            // Past the limit the dashboard would have refused to load, so the sheet is left off rather than
            // slowing the page down. It is looked at again next check, in case rows were deleted meanwhile.
            if (rowCount + rows.length > MAX_ROWS) {
              break;
            }

            rowCount += rows.length;
            sheet.rowCount = rows.length;
            added.push(sheet);
            addedRowsBySheetId[sheet.id] = rows;
          }

          if (added.length > 0) {
            this.setState(prevState => ({
              sheets: prevState.sheets
                .filter(sheet => !addedRowsBySheetId[sheet.id])
                .concat(added)
                .sort((a, b) => b.dates.lastModified - a.dates.lastModified),
              rowsBySheetId: {...prevState.rowsBySheetId, ...addedRowsBySheetId}
            }));
          }
        }
      }
    }
    catch (e) {
      if (e instanceof CancelledException) {
        return;
      }

      // The dashboard keeps what it has, and the next check tries again.
      console.error(e);
    }

    this.scheduleNewSheetsCheck();
  }

  handleRowChanged = (sheet, row) => {
    // Update the state for the changed row.
    this.setState(prevState => ({
      rowsBySheetId: {
        ...prevState.rowsBySheetId,
        [sheet.id]: prevState.rowsBySheetId[sheet.id].map(r => r.id === row.id ? row : r)
      }
    }));
  }

  handleRowDeleted = (sheet, row) => {
    if (this.rowsDeletedDuringLoad) {
      this.rowsDeletedDuringLoad.add(row.id);
    }

    // Remove the row for that sheet.
    this.setState(prevState => ({
      rowsBySheetId: {
        ...prevState.rowsBySheetId,
        [sheet.id]: prevState.rowsBySheetId[sheet.id].filter(r => r.id !== row.id)
      }
    }));
  }

  handleAnswersChanged = (sheet, row, question, answers) => {
    let {sheets, rowsBySheetId} = this.state;

    // Update the sheets state if the question is a context question.
    if (question.context) {
      sheets = sheets.map(s => {
        if (s.id === sheet.id) {
          return {
            ...s,
            contextAnswers: s.contextAnswers.filter(answer => answer.questionId !== question.id).concat(answers)
          };
        }

        return s;
      });
    }

    // Update the rowsBySheetId state if the question is not a context question.
    if (!question.context) {
      rowsBySheetId = {
        ...rowsBySheetId,
        [sheet.id]: rowsBySheetId[sheet.id].map(r => {
          if (r.id === row.id) {
            return {
              ...r,
              answers: r.answers.filter(answer => answer.questionId !== question.id).concat(answers)
            };
          }

          return r;
        })
      };
    }

    // Commit the state changes.
    this.setState({sheets, rowsBySheetId});
  }

  handleExportSheets = async () => {
    const {credentials} = this.props;
    const {sheets, template, exportDeleted} = this.state;
    const now = new Date();
    const {match: {params: {sheetIds}}} = this.props;
    let sheetIdsAsList = sheetIds.split(',');

    if (sheetIds === 'all') {
      sheetIdsAsList = [];

      for (let i = 0; i < this.countSheets(); i++) {
        sheetIdsAsList[i] = sheets[i].id;
      }
    }

    this.setState({exportSheets: true});

    try {
      const content = await api.exportMultiSheetData(sheetIdsAsList, exportDeleted, credentials);
      FileSaver.saveAs(content, `${template.name}-${now.getTime()}.xlsx`);
    }
    catch (e) {
      if(e.status === 400) {
        NotificationManager.error('Too much data to export. Reduce your date range and try again.');
      }
      else {
        NotificationManager.error('Unable to export data. Please try again.');
      }
    }
    finally {
      this.setState({exportSheets: false});
    }
  }

  handleApplyFilters = filters => {
    // Asked here rather than only in load, so turning it down leaves the filters as they were.
    if (!this.confirmStopBulkDelete()) {
      return;
    }

    this.setState({filterRows: false, filters});
  }

  updateTemplate = (template, onComplete = () => null) => {
    this.setState({template}, onComplete);
  }

  toggleActions = () => {
    this.setState(prevState => ({actionsOpen: !prevState.actionsOpen}));
  }

  toggleSelectSheets = () => {
    this.setState(prevState => ({manageSheets: !prevState.manageSheets}));
  }

  toggleFilters = () => {
    this.setState(prevState => ({filterRows: !prevState.filterRows}));
  }

  toggleFormExport = () => {
    this.setState(prevState => ({exportForm: !prevState.exportForm}));
  }

  toggleShowDeleted =() => {
    this.setState(prevState => ({showDeleted: !prevState.showDeleted}))
  }

  toggleExportDeleted = () => {
    this.setState(prevState => ({exportDeleted: !prevState.exportDeleted}))
  }

  // Snapshot the ids so row edits can't change a running job.
  openBulkPrint = () => {
    this.setState(prevState => ({bulkPrintRowIds: getViewDataRowIds(prevState.rowsBySheetId)}));
  }

  closeBulkPrint = () => {
    this.setState({bulkPrintRowIds: null});
  }

  openBulkReview = bulkReviewState => {
    this.setState({bulkReviewState, actionsOpen: false});
  }

  closeBulkReview = () => {
    this.setState({bulkReviewState: null});
  }

  handleBulkReviewComplete = reviewed => {
    this.setState({bulkReviewState: null});

    if (reviewed > 0) {
      // The review touched responses across several sheets, so reload to pick up their new state.
      this.load();
    }
  }

  // Bulk review works off the responses this screen has loaded, so it is only offered when they are all here.
  canBulkReview = () => {
    const {user} = this.props;
    const {template, tooManyRows} = this.state;

    return !tooManyRows
      && template.reviewAllEnabled !== false
      && template.reviewers.length > 0
      && (CanReviewTemplateData(user, template)
        || (template.secondLevelReviewEnabled && CanSecondLevelReviewTemplateData(user, template)));
  }

  countSheets = () => {
    const {sheets} = this.state;
    const {match: {params: {sheetIds}}} = this.props;

    if (!sheets) {
      if (sheetIds === 'all') {
        return 0;
      }

      return sheetIds.split(',').length;
    }
    
    return sheets.length;
  }

  renderDashboard = () => {
    const {sheets, template, rowsBySheetId, filters, catalog, pinnedWidgets, showDeleted} = this.state;

    return (
      <SheetDashboardTab
        sheets={sheets}
        template={template}
        rows={rowsBySheetId}
        filters={filters}
        catalog={catalog}
        onTemplateChanged={this.updateTemplate}
        onApplyFilters={this.handleApplyFilters}
        pinnedWidgets={pinnedWidgets}
        showDeleted={showDeleted}
      />
    );
  }

  renderViewDataTab = () => {
    const {sheets, template, rowsBySheetId, filters, catalog, showDeleted} = this.state;

    return (
      <ViewDataTab
        sheets={sheets}
        template={template}
        rows={rowsBySheetId}
        filters={filters}
        catalog={catalog}
        onRowChange={this.handleRowChanged}
        onRowDeleted={this.handleRowDeleted}
        onAnswersChange={this.handleAnswersChanged}
        onApplyFilters={this.handleApplyFilters}
        onBulkReview={this.canBulkReview() ? this.openBulkReview : null}
        onBulkDeleteChange={this.handleBulkDeleteChange}
        showDeleted={showDeleted}
      />
    );
  }

  renderContainer = isSmallScreen => {
    const {showDeleted, loading, numberOfSheetsLoaded, failed, tooManyRows, template, rowsBySheetId, filters, catalog, actionsOpen, manageSheets, filterRows, exportForm, exportSheets, exportDeleted, bulkReviewState, bulkPrintRowIds, bulkDeleting} = this.state;
    const numberOfSelectedSheets = this.countSheets();

    if (loading) {
      return (
        <Screen activeLink="Dashboard">
          <Loading>
            {numberOfSelectedSheets === 0 
              ? <>Loading...</>
              : <>Loaded {numberOfSheetsLoaded} of {numberOfSelectedSheets} sheets...</>
            }
          </Loading>
        </Screen>
      );
    }

    if (failed) {
      return (
        <Screen activeLink="Dashboard">
          <Error message="Unable to load sheet" />
        </Screen>
      );
    }

    const {location, user, credentials, match: {params: {templateId, sheetIds}}} = this.props;
    const dashboardUrl = `/sheet/${templateId}/${sheetIds}`;
    const dataUrl = `/sheet/${templateId}/${sheetIds}/data`;
    const sheetIdsAsList = sheetIds.split(',');
    // rowsBySheetId can be stale when tooManyRows is set, so bulk printing is hidden in that case.
    const canBulkPrint = !tooManyRows
      && location.pathname.indexOf(dataUrl) === 0
      && user.role === 'ADMIN'
      && !!flags.isOn(user, 'SCALE_PRINT_ENABLED')
      && !!template.labelLayoutId;
    const viewDataRowCount = canBulkPrint
      ? Object.keys(rowsBySheetId).reduce((sum, sheetId) => sum + rowsBySheetId[sheetId].length, 0)
      : 0;

    return (
      <Screen
        title={<T noTranslate>{template.name}</T>}
        windowTitle={template.name}
        activeLink="Dashboard"
        actions={!isSmallScreen ? [
          // The header shows one action on a wide screen, so the date range shares it with Options to sit beside it.
          // Changing the date range, filtering, picking sheets and bulk review all reload the screen, which would stop a
          // running bulk delete.
          <HeaderActions>
            <SheetDateRange inline disabled={bulkDeleting} filters={filters} onApplyFilters={this.handleApplyFilters} />
            <Button id="dashboard-actions" disabled={bulkDeleting} onClick={this.toggleActions}>
              Options <Icon name="chevron-down" />
            </Button>
          </HeaderActions>
        ] : [
          <Button disabled={bulkDeleting} onClick={this.toggleFilters}>Filter Data</Button>,
          [...template.contextQuestions, ...template.questions].some(question => question.deleted) && <Button onClick={this.toggleShowDeleted}>
            {showDeleted ? "Hide deleted question data" : "Show deleted question data"}</Button>,
          <Button disabled={bulkDeleting} onClick={this.toggleSelectSheets}><T isolate>Select Sheets</T> ({numberOfSelectedSheets})</Button>,
          this.canBulkReview() && <Button disabled={bulkDeleting} onClick={() => this.openBulkReview('APPROVED')}>Approve All Data</Button>,
          this.canBulkReview() && <Button disabled={bulkDeleting} onClick={() => this.openBulkReview('REJECTED')}>Reject All Data</Button>
        ]}
        tabs={!tooManyRows && (
          <Tabs>
            <Tabs.Tab to={dashboardUrl} active={location.pathname === dashboardUrl}>Dashboard</Tabs.Tab>
            <Tabs.Tab to={dataUrl} active={location.pathname.indexOf(dataUrl) === 0}>View Data</Tabs.Tab>
          </Tabs>
        )}>
        {tooManyRows ? (
          <>
            <Content>
              <Notification error>
                There is too much data to display. The maximum amount of data that can be displayed is {MAX_ROWS.toLocaleString()} sheet responses or {MAX_SHEETS.toLocaleString()} sheets.
                Please select less sheets with less responses or apply filters to continue.
              </Notification>
            </Content>
            {/* Narrowing the date range is the usual way out of having too much data. On a wide screen it is in the
                header. */}
            {isSmallScreen && <SheetDateRange disabled={bulkDeleting} filters={filters} onApplyFilters={this.handleApplyFilters} />}
          </>
        ) : (
          <>
            <FilterInfoMessage
              numberOfSheets={numberOfSelectedSheets}
              sheetIds={sheetIdsAsList}
              filters={filters}
            />
            {/* A small screen's header actions are a list of buttons, so the date range stays on the page there. */}
            {isSmallScreen && <SheetDateRange disabled={bulkDeleting} filters={filters} onApplyFilters={this.handleApplyFilters} />}
            <Switch>
              <Route path="/sheet/:templateId/:sheetIds/data" component={this.renderViewDataTab} />
              <Route path="/sheet/:templateId/:sheetIds" component={this.renderDashboard} />
            </Switch>
          </>
        )}
        <Menu
          open={actionsOpen} 
          position="bottom-end"  
          offsetY={5} 
          relativeTo="dashboard-actions" 
          onClose={this.toggleActions}>
          <Menu.Section>
            <Menu.Text>Filter Options</Menu.Text>
            <Menu.Divider />
            <Menu.Option onClick={this.toggleFilters}>Filter data</Menu.Option>
            {[...template.contextQuestions, ...template.questions].some(question => question.deleted) && (
              <Menu.Option keepOpen onClick={this.toggleShowDeleted}> {showDeleted ? "Hide deleted question data" : "Show deleted question data"}</Menu.Option>
            )}
            <Menu.Option onClick={this.toggleSelectSheets}><T isolate>Select sheets</T> ({numberOfSelectedSheets})</Menu.Option>
          </Menu.Section>
          {this.canBulkReview() && (
            <Menu.Section>
              <Menu.Text>Review Options</Menu.Text>
              <Menu.Divider />
              <Menu.Option onClick={() => this.openBulkReview('APPROVED')}>Approve filtered pending rows</Menu.Option>
              <Menu.Option onClick={() => this.openBulkReview('REJECTED')}>Reject filtered pending rows</Menu.Option>
            </Menu.Section>
          )}
          {user.role !== "USER" &&
          <Menu.Section>
            <Menu.Text>Export Options</Menu.Text>
            <Menu.Divider />
            <Menu.Option disabled={exportSheets} onClick={this.handleExportSheets}>
              {exportSheets ? 'Exporting...' : <T isolate>Export <T ignore>{numberOfSelectedSheets}</T> sheet(s)</T>}
            </Menu.Option>
            {[...template.contextQuestions, ...template.questions].some(question => question.deleted) && (
              <Menu.Option keepOpen active={exportDeleted} onClick={this.toggleExportDeleted}>
                Include deleted question data {exportDeleted ? "✓ " : "✖ "}
              </Menu.Option>
            )}
            <Menu.Option onClick={this.toggleFormExport}>Export all form data</Menu.Option>
          </Menu.Section>}
          {canBulkPrint &&
          <Menu.Section>
            <Menu.Text>Label Options</Menu.Text>
            <Menu.Divider />
            <Menu.Option disabled={viewDataRowCount === 0} onClick={this.openBulkPrint}>
              Print labels ({viewDataRowCount.toLocaleString()} {viewDataRowCount === 1 ? 'row' : 'rows'})
            </Menu.Option>
          </Menu.Section>}
        </Menu>
        <SheetSelector
          open={manageSheets}
          onClose={this.toggleSelectSheets}
          template={template}
          selectedIds={sheetIdsAsList}
        />
        <SheetRowFilter
          open={filterRows}
          onClose={this.toggleFilters}
          template={template}
          catalog={catalog}
          filters={filters}
          onApplyFilters={this.handleApplyFilters}
        />
        <ExportFormDataModal
          form={exportForm && template}
          onClose={this.toggleFormExport}
          includeDeletedAnswers={exportDeleted}
        />
        <BulkReviewModal
          template={template}
          rows={rowsBySheetId}
          reviewState={bulkReviewState}
          onClose={this.closeBulkReview}
          onComplete={this.handleBulkReviewComplete}
        />
        {bulkPrintRowIds && (
          <BulkPrintLabelsModal
            open
            templateId={templateId}
            rowIds={bulkPrintRowIds}
            credentials={credentials}
            onClose={this.closeBulkPrint}
          />
        )}
      </Screen>
    );
  }

  render() {
    return <MediaQuery maxWidth={800} children={this.renderContainer} />;
  }
}

// Row ids in View Data grid order. Must match the sort in public/workers/view-data-tab-worker.js.
export function getViewDataRowIds(rowsBySheetId) {
  return Object.keys(rowsBySheetId)
    .reduce((list, sheetId) => list.concat(rowsBySheetId[sheetId]), [])
    .sort((next, prev) => prev.dates.lastModified - next.dates.lastModified)
    .map(row => row.id);
}

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

export default withUser(SheetScreen);
